import { SEEK_ANIMATION } from '|>/shared/constants/rewind'
import { Events } from '|>/shared/events'
import { bind_, throttle, UPDATE_REFRESH_INTERVAL } from '|>/shared/fn'
import {
  findPosition,
  getPointerPosition,
  isSingleLeftClick,
} from '|>/shared/h'
import { Intent } from '|>/shared/types/intent'
import { on, register } from '|>/shared/vjs'
import { BasePlayProgressBar, BaseSlider } from '../../base'
import { MouseBar } from '../mouse-bar'
import { MouseTimeDisplay } from '../mouse-time-display'

import './vod-seek-bar.css'

const MANY_CLICKS_WAIT_MS = 400

/**
 * Vod seekable bar
 */
@register
export class VodSeekBar extends BaseSlider {
  declare playerEvent: string
  declare mouseBar: MouseBar
  declare mouseTimeDisplay: MouseTimeDisplay

  private updateInterval: ReturnType<typeof setInterval> | null = null

  totalShift: number = 0
  private accumulateTimeout: ReturnType<typeof setTimeout> | null = null
  private pendingSeekTime: number | null = null
  private savedProgressOnMouseUp: number | null = null

  constructor(player, options) {
    super(player, options)

    /* Handlers from this component is used by reference by rewind buttons and hotkeys */
    ;(player as any).vodSeekBar = this

    this.setEventHandlers()
  }

  override createEl() {
    return super.createEl(
      'div',
      { className: 'vjs-progress-holder vjs-x-vod-seek-bar' },
      { 'aria-label': this.localize('Vod Progress Bar') }
    )
  }

  @on(Events.Media.ContentChanged)
  contentChanged() {
    const loadProgressBar = this.getChild('loadProgressBar')
    if (loadProgressBar && loadProgressBar.el_) {
      loadProgressBar.el_.style.width = '0%'
    }

    // Reset vodProgressBar state
    const barEl = this.bar?.el() as HTMLElement
    if (barEl) {
      const sizeKey = this.vertical() ? 'height' : 'width'
      barEl.style[sizeKey] = '0%'
    }
  }

  setEventHandlers() {
    this.update_ = bind_(this, this.update)
    this.update = throttle(this.update_, UPDATE_REFRESH_INTERVAL)

    this.player_.on(['durationchange', 'timeupdate'], this.update)
    this.player_.on('ended', this.update_)

    this.updateInterval = null
    this.player_.on('playing', this.enableInterval)
    this.player_.on(['ended', 'pause', 'waiting'], this.disableInterval)
  }

  getPercent() {
    const currentTime = this.player_.scrubbing()
      ? this.player_.getCache().currentTime
      : this.player_.currentTime()

    return currentTime / this.player_.duration()
  }

  override update() {
    if (document.visibilityState === 'hidden') return 0

    if (!this.el_ || !this.bar) {
      return 0
    }

    // Use saved progress from mouseup if available, otherwise calculate normally
    const progress =
      this.savedProgressOnMouseUp !== null
        ? this.savedProgressOnMouseUp
        : this.getProgress()

    if (progress === this.progress_) {
      return progress
    }

    this.progress_ = progress

    this.requestNamedAnimationFrame('Slider#update', () => {
      // Set the new bar width or height
      const sizeKey = this.vertical() ? 'height' : 'width'

      // Convert to a percentage for css value
      const barEl = this.bar?.el() as HTMLElement
      if (barEl) barEl.style[sizeKey] = (progress * 100).toFixed(2) + '%'
    })

    return progress
  }

  enableInterval() {
    if (this.updateInterval) return

    this.updateInterval = setInterval(this.update, UPDATE_REFRESH_INTERVAL)
  }

  disableInterval(e?: Event) {
    if (
      this.player_.liveTracker &&
      this.player_.liveTracker.isLive() &&
      e &&
      e.type !== 'ended'
    )
      return

    if (!this.updateInterval) return

    clearInterval(this.updateInterval)
    this.updateInterval = null
  }

  override handleMouseDown(e) {
    // Save hover state on mousedown if parent is hovered
    const parentEl = this.parentComponent_.el()
    if (parentEl) {
      parentEl.classList.add('vjs-hover-active')
    }

    super.handleMouseDown(e)
  }

  override handleMouseMove(event, mouseDown = false) {
    if (!isSingleLeftClick(event) || isNaN(this.player_.duration())) return

    if (!mouseDown && !this.player_.scrubbing()) {
      this.player_.scrubbing(true)
    }

    let newTime
    const distance = this.calculateDistance(event)

    newTime = distance * this.player_.duration()

    // Don't let video end while scrubbing.
    if (newTime === this.player_.duration()) {
      newTime = newTime - 0.1
    }

    this.savedProgressOnMouseUp = distance
    // Save time for seek on mouseup
    this.pendingSeekTime = newTime

    if (this.mouseBar && this.mouseTimeDisplay) {
      const rect = findPosition(this.el_)
      const point = getPointerPosition(this.el_, event).x
      const pointPx = point * rect.width

      // Update mouseBar width
      this.mouseBar.update(rect, point)

      // Update mouseTimeDisplay position
      this.mouseTimeDisplay.updateTooltip(
        rect,
        point,
        pointPx,
        0,
        this.player_.duration() * 1000
      )
    }

    this.update()
  }

  override handleMouseUp(e) {
    const parentEl = this.parentComponent_.el()
    if (parentEl) {
      parentEl.classList.remove('vjs-hover-active')
    }

    // Perform seek only on mouseup
    if (this.pendingSeekTime !== null) {
      this.doSeek(this.pendingSeekTime)
      this.pendingSeekTime = null
    }

    super.handleMouseUp(e)
  }

  trySeek(params: {
    event?: Event
    shiftSec?: number
    escapeThrottle?: boolean
    animation?: string
  }) {
    const { shiftSec, escapeThrottle = true, animation } = params

    if (animation === SEEK_ANIMATION.TAP)
      this.player_.tapHandler.animateSeek(shiftSec)
    if (animation === SEEK_ANIMATION.CLICK)
      this.player_.arrowSeekHandlers.animateSeek(shiftSec)

    if (shiftSec) {
      this.update() // updates playback position if seeking while in pause
      return this.throttledSeek(shiftSec, escapeThrottle)
    }
  }

  private throttledSeek(shift: number, escapeThrottle: boolean = false) {
    this.totalShift += shift

    if (escapeThrottle) return this.doSeek(this.player_.currentTime() + shift)

    // Reset seek call timeout if user clicks again within MANY_CLICKS_WAIT_MS
    if (this.accumulateTimeout) clearTimeout(this.accumulateTimeout)

    this.accumulateTimeout = setTimeout(() => {
      this.doSeek(this.player_.currentTime() + this.totalShift)
      this.totalShift = 0
    }, MANY_CLICKS_WAIT_MS)
  }

  private doSeek(position: number) {
    this.player_.trigger(Events.User.Intent, Intent.Seek)
    this.player_.currentTime(position)
    this.savedProgressOnMouseUp = null
  }

  override dispose() {
    this.disableInterval()

    this.player_.off(['durationchange', 'timeupdate'], this.update)
    this.player_.off('ended', this.update_)

    this.player_.off('playing', this.enableInterval)
    this.player_.off(['ended', 'pause', 'waiting'], this.disableInterval)

    super.dispose()
  }
}
VodSeekBar.prototype.playerEvent = 'mousemove'

VodSeekBar.options = {
  children: [
    MouseBar.as('mouseBar'),
    MouseTimeDisplay.as('mouseTimeDisplay'),
    'loadProgressBar',
    BasePlayProgressBar.as('vodProgressBar'),
  ],
  barName: 'vodProgressBar',
}
